import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";
import type {
  AdminListResponse,
  KnowledgeAction,
  KnowledgeItemDto,
  ListParams,
  PolicyAction,
  PolicyDto,
  PolicyVersionDto,
  RequestMeta,
} from "./types";

const BASE = `${API_VERSION_PREFIX}/hq/admin`;

function toQuery(params: ListParams = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createKnowledgeApi(client: ApiClient) {
  return {
    listKnowledge(params: ListParams = {}) {
      return client.get<AdminListResponse<KnowledgeItemDto>>(
        `${BASE}/knowledge-items${toQuery(params)}`,
      );
    },
    getKnowledge(id: string) {
      return client.get<{ item: KnowledgeItemDto; meta: RequestMeta }>(
        `${BASE}/knowledge-items/${id}`,
      );
    },
    createKnowledge(body: Record<string, unknown>) {
      return client.post<{ item: KnowledgeItemDto; meta: RequestMeta }>(
        `${BASE}/knowledge-items`,
        body,
        newIdempotencyKey(),
      );
    },
    applyKnowledgeAction(id: string, action: KnowledgeAction) {
      return client.post(
        `${BASE}/knowledge-items/${id}/actions/${action}`,
        {},
        newIdempotencyKey(),
      );
    },

    listPolicies(params: ListParams = {}) {
      return client.get<AdminListResponse<PolicyDto>>(
        `${BASE}/policies${toQuery(params)}`,
      );
    },
    getPolicy(id: string) {
      return client.get<{
        policy: PolicyDto;
        versions: PolicyVersionDto[];
        meta: RequestMeta;
      }>(`${BASE}/policies/${id}`);
    },
    createPolicy(body: Record<string, unknown>) {
      return client.post<{ policy: PolicyDto; meta: RequestMeta }>(
        `${BASE}/policies`,
        body,
        newIdempotencyKey(),
      );
    },
    applyPolicyAction(
      id: string,
      action: PolicyAction,
      body: Record<string, unknown> = {},
    ) {
      return client.post(
        `${BASE}/policies/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },
    createPolicyVersion(policyId: string, body: Record<string, unknown>) {
      return client.post<{ version: PolicyVersionDto; meta: RequestMeta }>(
        `${BASE}/policies/${policyId}/versions`,
        body,
        newIdempotencyKey(),
      );
    },
    applyPolicyVersionAction(
      policyId: string,
      versionId: string,
      action: PolicyAction,
    ) {
      return client.post(
        `${BASE}/policies/${policyId}/versions/${versionId}/actions/${action}`,
        {},
        newIdempotencyKey(),
      );
    },
  };
}

export type KnowledgeApi = ReturnType<typeof createKnowledgeApi>;
